// @flow

import * as React from 'react';
import {useContext, useMemo} from 'react';
import {contextStubFunction} from 'docs/util';
import {createRandomValueGenerator} from './createRandomValueGenerator';
import type {RandomValueGenerator} from './createRandomValueGenerator';

export type RandomContextType = RandomValueGenerator;

const contextStub: any = {
  chooseRandomItem: contextStubFunction('Random'),
  generateRandom: contextStubFunction('Random'),
  generateRandomInteger: contextStubFunction('Random'),
  generateRandomString: contextStubFunction('Random'),
};

const RandomContext = React.createContext<RandomContextType>(contextStub);

type RandomProviderProps = {|
  children: React.Node,
  seed: string,
|};

// If any components require randomization, the numbers generated must match between
// the server (during the initial render) and the client (during hydration). As a result,
// we need to generate stable predictable random numbers for a given seed. This seed
// is generated on the server and passed to the client as part of the initial state.
// Unless you're certain that a component will only be rendered exclusively on the
// client, you should use this provider to generate random numbers.
// (See also TimeProvider.)
export const RandomProvider = ({children, seed}: RandomProviderProps) => {
  const generator = useMemo(() => createRandomValueGenerator(seed), [seed]);

  return (
    <RandomContext.Provider value={generator}>
      {children}
    </RandomContext.Provider>
  );
};

export const useRandom = () => {
  return useContext(RandomContext);
};
